import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { t } from 'i18next'

import { formatDateToRequest } from '~/helpers'
import { getCampaign } from '~/modules/retailMedia/store/campaign/actions'
import { patchDestinationUrls } from '~/modules/retailMedia/store/industry/campaign/actions'
import { useAppSelector } from '~/store/hooks'

import { DTOFormatDestinations } from '../../../../CampaignForm/strategies/helpers'
import DestinationUrlsEditor, {
  DestinationUrlEditorEntry
} from '../DestinationUrlsEditor'

const EditFormDestinationURL = ({
  data,
  onDisableEditMode,
  destinationsConfig,
  isMultiDestination
}: {
  data: CampaignView
  onDisableEditMode: () => void
  destinationsConfig: DestinationUrlsConfig[]
  isMultiDestination: boolean
}) => {
  const dispatch = useDispatch()

  const { id } = data

  const initialEntries = useMemo<DestinationUrlEditorEntry[]>(() => {
    if (!isMultiDestination) {
      return [{ url: data.destinationUrl || '' }]
    }

    return (destinationsConfig || [])
      .map(config => {
        const saved = data.destinationUrls?.find(
          destination =>
            destination.publisher_id === config.publisher_id &&
            destination.brand === config.code
        )

        return {
          url: saved?.url || '',
          brand: config.code,
          name: config.name,
          publisher_id: config.publisher_id
        }
      })
      .sort((a, b) => {
        if (!!a.url !== !!b.url) return a.url ? -1 : 1
        return (a.name || '').localeCompare(b.name || '')
      })
  }, [
    data.destinationUrl,
    data.destinationUrls,
    destinationsConfig,
    isMultiDestination
  ])

  const [entries, setEntries] = useState(initialEntries)

  useEffect(() => {
    setEntries(initialEntries)
  }, [initialEntries])

  /**
   * Handle submit
   * Método antigo de revalidação
   */
  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)

  const {
    industry: {
      campaign: {
        patch: { loadingKeys: loadingPatch }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  const patchKey = 'destination_url'
  const loadingSave = useMemo(
    () => loadingPatch?.includes(patchKey),
    [loadingPatch]
  )

  const queryParams = useMemo(
    () => ({
      start_date: formatDateToRequest(startDate),
      end_date: formatDateToRequest(endDate),
      campaign_id: id
    }),
    [id, startDate, endDate]
  )

  const handleSuccess = useCallback(() => {
    dispatch(
      getCampaign({
        ...queryParams,
        account_info: true,
        requestInBackground: true
      })
    )
  }, [dispatch, queryParams])

  const handleSubmit = useCallback(() => {
    const destinations = isMultiDestination
      ? entries.reduce<
          Record<string, { url: string; brand: string; publisher_id: string }>
        >((result, entry, index) => {
          result[`${entry.publisher_id}-${entry.brand ?? index}`] = {
            url: entry.url,
            brand: entry.brand || '',
            publisher_id: entry.publisher_id || ''
          }
          return result
        }, {})
      : undefined

    const body: UpdateDestinationUrlBody = {
      url: isMultiDestination ? null : entries[0]?.url || null,
      destinations: destinations ? DTOFormatDestinations(destinations) : null
    }

    dispatch(
      patchDestinationUrls({
        body,
        id: data?.id,
        patchKey,
        successFeedbackMsg: t('rm:campaign.successfully.updated'),
        onSuccess: () => {
          handleSuccess()
          onDisableEditMode?.()
        }
      })
    )
  }, [
    data?.id,
    dispatch,
    entries,
    handleSuccess,
    isMultiDestination,
    onDisableEditMode
  ])

  return (
    <DestinationUrlsEditor
      entries={entries}
      onChange={(index, url) =>
        setEntries(current =>
          current.map((entry, entryIndex) =>
            entryIndex === index ? { ...entry, url } : entry
          )
        )
      }
      onCancel={onDisableEditMode}
      onSubmit={handleSubmit}
      isSaving={loadingSave}
      showHelperText
    />
  )
}

export default EditFormDestinationURL
